import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { tenantsAPI } from '../services/api';

export function TenantsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    allowed_domains: '',
    plan_id: '',
  });

  // [2026-03-22] 요금제 목록
  const { data: plansData } = useQuery({
    queryKey: ['plans'],
    queryFn: () => tenantsAPI.listPlans(),
  });
  const plans = plansData?.data || [];

  const { data, isLoading } = useQuery({
    queryKey: ['tenants'],
    queryFn: () => tenantsAPI.list(1, 100),
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => tenantsAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      setShowModal(false);
      setFormData({ name: '', slug: '', description: '', allowed_domains: '', plan_id: '' });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate({
      name: formData.name,
      slug: formData.slug,
      description: formData.description || undefined,
      plan_id: formData.plan_id || undefined,
      allowed_domains: formData.allowed_domains
        .split(',')
        .map((d) => d.trim())
        .filter(Boolean),
    });
  };

  const tenants = data?.data?.items || [];

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">테넌트 관리</h1>
          <p className="text-gray-600">테넌트를 관리하고 API 키를 발급합니다.</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          + 테넌트 추가
        </button>
      </div>

      <div className="bg-white rounded-lg shadow">
        {isLoading ? (
          <div className="p-6 text-center text-gray-500">로딩 중...</div>
        ) : tenants.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            등록된 테넌트가 없습니다.
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  테넌트명
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  슬러그
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  요금제
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  상태
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  문서/대화
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  생성일
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  작업
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {tenants.map((tenant: any) => (
                <tr
                  key={tenant.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/tenants/${tenant.id}`)}
                >
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">{tenant.name}</div>
                    {tenant.description && (
                      <div className="text-sm text-gray-500 truncate max-w-xs">
                        {tenant.description}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-gray-600">{tenant.slug}</td>
                  <td className="px-6 py-4">
                    {(() => {
                      const plan = plans.find((p: any) => p.id === tenant.plan_id);
                      return plan ? (
                        <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded font-medium">
                          {plan.display_name}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm">미설정</span>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        tenant.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {tenant.is_active ? '활성' : '비활성'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    {tenant.document_count || 0} / {tenant.conversation_count || 0}
                  </td>
                  <td className="px-6 py-4 text-gray-500">
                    {new Date(tenant.created_at).toLocaleDateString('ko-KR')}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/tenants/${tenant.id}`);
                      }}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      상세보기
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-lg font-semibold mb-4">테넌트 추가</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  테넌트명 *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  슬러그 *
                </label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) =>
                    setFormData({ ...formData, slug: e.target.value })
                  }
                  required
                  pattern="^[a-z0-9-]+$"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="영문 소문자, 숫자, 하이픈만 사용"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  설명
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  허용 도메인
                </label>
                <input
                  type="text"
                  value={formData.allowed_domains}
                  onChange={(e) =>
                    setFormData({ ...formData, allowed_domains: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="example.com, localhost (쉼표로 구분)"
                />
              </div>
              {/* [2026-03-22] 요금제 선택 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">요금제</label>
                <select
                  value={formData.plan_id}
                  onChange={(e) => setFormData({ ...formData, plan_id: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  <option value="">선택 안 함</option>
                  {plans.map((p: any) => (
                    <option key={p.id} value={p.id}>
                      {p.display_name} (메시지 {p.max_messages_per_month === -1 ? '무제한' : p.max_messages_per_month}, 문서 {p.max_documents === -1 ? '무제한' : p.max_documents})
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition"
                >
                  취소
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
                >
                  {createMutation.isPending ? '생성 중...' : '생성'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
